import { useEffect, useMemo, useState } from "preact/hooks";
import { queryOnce, transact, tx, useAuth, useQuery } from "./db.ts";
import { ErrorMessage, GroupSelector, Loading } from "./uiComponents.tsx";
import { t } from "./theme.ts";
import { empty, letIn } from "@uri/gamla";
import { id } from "@instantdb/react";

import { getApiBase } from "./config.ts";

const apiBase = getApiBase();

const periods = [
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
] as const;

type DayPoint = { date: string; value: number };
type LatencyPoint = { date: string; avgMs: number; count: number };

type AnalyticsResponse = {
  ok?: boolean;
  chatsPerDay?: DayPoint[];
  participantsPerDay?: DayPoint[];
  messagesPerDay?: DayPoint[];
  latencyByParticipant?: Record<string, LatencyPoint[]>;
  error?: string;
};

const formatDateLabel = (dateStr: string): string => {
  const [, m, d] = dateStr.split("-");
  return `${m}/${d}`;
};

const callApi = async (
  endpoint: string,
  token: string,
  payload: Record<string, unknown> = {},
): Promise<AnalyticsResponse> => {
  try {
    const r = await fetch(apiBase, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint, token, payload }),
    });

    if (!r.ok) {
      const text = await r.text();
      throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
    }

    return r.json();
  } catch (e) {
    if (e instanceof Error && e.message === "Failed to fetch") {
      throw new Error(
        `Network error connecting to ${apiBase}. Please check if the server is running.`,
      );
    }
    throw e;
  }
};

type BarChartProps = {
  data: { label: string; value: number }[];
  title: string;
  color: string;
};

const chartHeight = 200;
const barGap = 2;

const BarChart = ({ data, title, color }: BarChartProps) => {
  const th = t();
  const maxValue = Math.max(...data.map((d) => d.value), 1);
  const labelEvery = data.length <= 14 ? 1 : data.length <= 30 ? 5 : 15;
  const yTicks = Array.from(
    { length: 5 },
    (_, i) => Math.round((maxValue * i) / 4),
  );

  const leftPad = 40;
  const bottomPad = 40;
  const chartWidth = Math.max(data.length * 16, 300);
  const svgWidth = chartWidth + leftPad + 10;
  const svgHeight = chartHeight + bottomPad + 20;

  const barWidth = Math.max(
    (chartWidth - barGap * data.length) / data.length,
    4,
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: th.textMuted,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div style={{ overflowX: "auto", maxWidth: "100%" }}>
        <svg
          width={svgWidth}
          height={svgHeight}
          style={{ display: "block" }}
        >
          {yTicks.map((tick, i) => {
            const y = chartHeight - (tick / maxValue) * chartHeight + 10;
            return (
              <g key={`tick-${i}`}>
                <line
                  x1={leftPad}
                  x2={svgWidth - 10}
                  y1={y}
                  y2={y}
                  stroke={th.gridLine}
                  stroke-dasharray="3,3"
                />
                <text
                  x={leftPad - 6}
                  y={y + 4}
                  text-anchor="end"
                  fill={th.axisText}
                  font-size="11"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {data.map((d, i) => {
            const barH = (d.value / maxValue) * chartHeight;
            const x = leftPad + i * (barWidth + barGap);
            const y = chartHeight - barH + 10;
            return (
              <g key={`bar-${i}`}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barH}
                  fill={color}
                  rx={2}
                  opacity={0.85}
                >
                  <title>
                    {d.label}: {d.value}
                  </title>
                </rect>
                {i % labelEvery === 0 && (
                  <text
                    x={x + barWidth / 2}
                    y={chartHeight + 24}
                    text-anchor="middle"
                    fill={th.axisText}
                    font-size="10"
                    transform={`rotate(-45, ${x + barWidth / 2}, ${
                      chartHeight + 24
                    })`}
                  >
                    {formatDateLabel(d.label)}
                  </text>
                )}
              </g>
            );
          })}

          <line
            x1={leftPad}
            x2={svgWidth - 10}
            y1={chartHeight + 10}
            y2={chartHeight + 10}
            stroke={th.axisLine}
          />
        </svg>
      </div>
    </div>
  );
};

const formatLatency = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(0)}s`;
  const m = s / 60;
  return `${m.toFixed(1)}min`;
};

const latencyChartColors = [
  "#3a7bd5",
  "#2ec4a0",
  "#ff9f43",
  "#e74c8b",
  "#9b59b6",
  "#1abc9c",
  "#e67e22",
  "#2ecc71",
  "#e74c3c",
  "#3498db",
];

type LatencyChartProps = {
  data: Record<string, LatencyPoint[]>;
  dates: string[];
};

const LatencyChart = ({ data, dates }: LatencyChartProps) => {
  const th = t();
  const participants = Object.keys(data);
  if (participants.length === 0) return null;

  // Build a lookup: participant -> date -> avgMs
  const lookup: Record<string, Record<string, number>> = {};
  let maxMs = 1;
  for (const p of participants) {
    lookup[p] = {};
    for (const entry of data[p]) {
      lookup[p][entry.date] = entry.avgMs;
      if (entry.avgMs > maxMs) maxMs = entry.avgMs;
    }
  }

  const leftPad = 50;
  const bottomPad = 40;
  const rightPad = 10;
  const pointSpacing = Math.max(16, Math.min(40, 600 / dates.length));
  const chartW = dates.length * pointSpacing;
  const svgWidth = chartW + leftPad + rightPad;
  const svgHeight = chartHeight + bottomPad + 20;
  const labelEvery = dates.length <= 14 ? 1 : dates.length <= 30 ? 5 : 15;

  // Y ticks in ms
  const yTicks = Array.from(
    { length: 5 },
    (_, i) => Math.round((maxMs * i) / 4),
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: th.textMuted,
          marginBottom: 8,
        }}
      >
        Avg response latency (top participants)
      </div>
      <div style={{ overflowX: "auto", maxWidth: "100%" }}>
        <svg width={svgWidth} height={svgHeight} style={{ display: "block" }}>
          {/* Grid lines */}
          {yTicks.map((tick, i) => {
            const y = chartHeight - (tick / maxMs) * chartHeight + 10;
            return (
              <g key={`lt-${i}`}>
                <line
                  x1={leftPad}
                  x2={svgWidth - rightPad}
                  y1={y}
                  y2={y}
                  stroke={th.gridLine}
                  stroke-dasharray="3,3"
                />
                <text
                  x={leftPad - 6}
                  y={y + 4}
                  text-anchor="end"
                  fill={th.axisText}
                  font-size="11"
                >
                  {formatLatency(tick)}
                </text>
              </g>
            );
          })}

          {/* X axis labels */}
          {dates.map((date, i) => {
            const x = leftPad + i * pointSpacing;
            return i % labelEvery === 0
              ? (
                <text
                  key={`xl-${i}`}
                  x={x}
                  y={chartHeight + 24}
                  text-anchor="middle"
                  fill={th.axisText}
                  font-size="10"
                  transform={`rotate(-45, ${x}, ${chartHeight + 24})`}
                >
                  {formatDateLabel(date)}
                </text>
              )
              : null;
          })}

          {/* Lines per participant */}
          {participants.map((p, pi) => {
            const color = latencyChartColors[pi % latencyChartColors.length];
            const points: { x: number; y: number; val: number }[] = [];
            for (let i = 0; i < dates.length; i++) {
              const val = lookup[p]?.[dates[i]];
              if (val !== undefined && val > 0) {
                points.push({
                  x: leftPad + i * pointSpacing,
                  y: chartHeight - (val / maxMs) * chartHeight + 10,
                  val,
                });
              }
            }
            if (points.length < 2) return null;

            const pathD = points
              .map((pt, i) => `${i === 0 ? "M" : "L"} ${pt.x} ${pt.y}`)
              .join(" ");

            return (
              <g key={`line-${pi}`}>
                <path
                  d={pathD}
                  fill="none"
                  stroke={color}
                  stroke-width={2}
                  opacity={0.85}
                />
                {points.map((pt, i) => (
                  <circle
                    key={`pt-${pi}-${i}`}
                    cx={pt.x}
                    cy={pt.y}
                    r={3}
                    fill={color}
                  >
                    <title>
                      {p}: {formatLatency(pt.val)}
                    </title>
                  </circle>
                ))}
              </g>
            );
          })}

          {/* X axis baseline */}
          <line
            x1={leftPad}
            x2={svgWidth - rightPad}
            y1={chartHeight + 10}
            y2={chartHeight + 10}
            stroke={th.axisLine}
          />
        </svg>
      </div>
      {/* Legend */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginTop: 8,
          fontSize: 12,
        }}
      >
        {participants.map((p, pi) => (
          <div
            key={p}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: latencyChartColors[pi % latencyChartColors.length],
              }}
            />
            <span style={{ color: th.textMuted }}>{p}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const userIdForEmail = (email: string) =>
  queryOnce({ users: { $: { where: { email } } } })
    .then(({ data: { users } }) =>
      empty(users)
        ? letIn(
          id(),
          (id) => transact(tx.users[id].update({ email })).then(() => id),
        )
        : users[0].id
    );

const addViewer = (groupId: string, email: string) =>
  userIdForEmail(email).then((uid) =>
    transact(tx.groups[groupId].link({ users: uid }))
  );

const removeViewer = (groupId: string, email: string) =>
  userIdForEmail(email).then((uid) =>
    transact(tx.groups[groupId].unlink({ users: uid }))
  );

const EmailList = (
  { items, onRemove, onAdd, label }: {
    items: { id: string; email: string }[];
    onRemove: (email: string) => void;
    onAdd: (email: string) => void;
    label: string;
  },
) => {
  const th = t();
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const handleAdd = () => {
    if (!email.trim()) return;
    onAdd(email.trim());
    setEmail("");
    setAdding(false);
  };

  const sectionHeaderStyle = {
    fontWeight: 500,
    fontSize: 14,
    color: th.textDim,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
  };

  return (
    <div>
      <div style={sectionHeaderStyle}>
        {label}
        <button
          type="button"
          className="compact-button"
          style={{ fontSize: 12, textTransform: "none" }}
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? "Cancel" : "+ Add"}
        </button>
      </div>
      {adding && (
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            className="input-field"
            style={{ flex: 1, marginBottom: 0 }}
          />
          <button
            type="button"
            className="compact-button"
            onClick={handleAdd}
          >
            Add
          </button>
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map(({ id, email }) => (
          <li
            key={id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 0",
              borderBottom: `1px solid ${th.border}`,
              fontSize: 14,
            }}
          >
            <span style={{ flex: 1, color: th.text }}>{email}</span>
            <button
              type="button"
              className="compact-button"
              style={{ fontSize: 12, color: th.danger }}
              onClick={() => onRemove(email)}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const GroupsPage = () => {
  const th = t();
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [periodDays, setPeriodDays] = useState(30);
  const [analytics, setAnalytics] = useState<
    {
      chatsPerDay: DayPoint[];
      participantsPerDay: DayPoint[];
      messagesPerDay: DayPoint[];
      latencyByParticipant: Record<string, LatencyPoint[]>;
    } | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Group renaming state
  const [editingName, setEditingName] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState("");

  // Tab navigation state
  const [activeTab, setActiveTab] = useState<"settings" | "analytics">(
    "settings",
  );

  const ownEmail = useAuth().user?.email;
  const { data, isLoading: accountsLoading, error: accountsError } = useQuery({
    accounts: { admins: {}, groups: { users: {} } },
  });

  const currentAccount = useMemo(
    () =>
      (data?.accounts ?? []).find((account) =>
        (account.admins ?? []).some((admin) => admin.email === ownEmail)
      ),
    [data, ownEmail],
  );

  const accountId = currentAccount?.id;
  const accountSecret = currentAccount?.secret;

  const groups = useMemo(
    () =>
      (currentAccount?.groups ?? []).map((g) => ({
        id: g.id,
        name: g.name,
        lastActivityAt: (g as Record<string, unknown>).lastActivityAt as
          | number
          | undefined,
        users: ((g as Record<string, unknown>).users as {
          id: string;
          email: string;
        }[]) || [],
      })),
    [accountId, currentAccount?.groups],
  );

  // Auto-select first group if none is selected
  useEffect(() => {
    if (!selectedGroupId && groups.length > 0) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, selectedGroupId]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedGroupId),
    [groups, selectedGroupId],
  );

  const selectedGroupViewers = selectedGroup?.users || [];

  const periodStart = useMemo(
    () => Date.now() - periodDays * 24 * 60 * 60 * 1000,
    [periodDays],
  );

  // Align tab with selected group: if All Groups is selected, default to analytics. Otherwise settings.
  useEffect(() => {
    if (!selectedGroupId) {
      setActiveTab("analytics");
    } else {
      setActiveTab("settings");
    }
  }, [selectedGroupId]);

  useEffect(() => {
    if (!accountSecret) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    callApi("getAnalytics", accountSecret, {
      since: periodStart,
      groupId: selectedGroupId || undefined,
    })
      .then((response: AnalyticsResponse) => {
        if (!response.ok) {
          throw new Error(response.error || "Failed to load analytics");
        }
        setAnalytics({
          chatsPerDay: response.chatsPerDay ?? [],
          participantsPerDay: response.participantsPerDay ?? [],
          messagesPerDay: response.messagesPerDay ?? [],
          latencyByParticipant: response.latencyByParticipant ?? {},
        });
      })
      .catch((e: Error) => {
        let errorMessage = e.message;
        if (
          e.message.includes("Failed to fetch") ||
          e.message.includes("Network error")
        ) {
          errorMessage =
            "Unable to connect to analytics server. Please check your internet connection and try again.";
        }
        console.error("Analytics fetch error:", e);
        setError(new Error(errorMessage));
      })
      .finally(() => setLoading(false));
  }, [accountSecret, periodStart, selectedGroupId]);

  const handleRenameGroup = () => {
    if (!groupNameInput.trim()) {
      setRenameError("Group name cannot be empty");
      return;
    }
    setRenaming(true);
    setRenameError("");
    callApi("renameGroup", accountSecret!, {
      groupId: selectedGroupId,
      name: groupNameInput.trim(),
    })
      .then((res: { ok?: boolean; error?: string }) => {
        if (res.ok) {
          setEditingName(false);
        } else {
          setRenameError(res.error || "Failed to rename group");
        }
      })
      .catch((e: Error) => setRenameError(e.message))
      .finally(() => setRenaming(false));
  };

  const firstDataDate = useMemo(() => {
    const latency = analytics?.latencyByParticipant ?? {};
    const latencyDates = new Set(
      Object.values(latency).flatMap((pts) =>
        pts.filter((p) => p.avgMs > 0).map((p) => p.date)
      ),
    );
    for (const d of analytics?.chatsPerDay ?? []) {
      if (
        d.value > 0 ||
        (analytics?.participantsPerDay ?? []).find((p) =>
          p.date === d.date && p.value > 0
        ) ||
        (analytics?.messagesPerDay ?? []).find((m) =>
          m.date === d.date && m.value > 0
        ) ||
        latencyDates.has(d.date)
      ) {
        return d.date;
      }
    }
    return null;
  }, [analytics]);

  const trimFrom = (points: DayPoint[]): DayPoint[] => {
    if (!firstDataDate) return points;
    const idx = points.findIndex((d) => d.date >= firstDataDate);
    return idx <= 0 ? points : points.slice(idx);
  };

  const chatsPerDay = useMemo(
    () =>
      trimFrom(analytics?.chatsPerDay ?? []).map((d) => ({
        label: d.date,
        value: d.value,
      })),
    [analytics, firstDataDate],
  );
  const usersPerDay = useMemo(
    () =>
      trimFrom(analytics?.participantsPerDay ?? []).map((d) => ({
        label: d.date,
        value: d.value,
      })),
    [analytics, firstDataDate],
  );
  const messagesPerDay = useMemo(
    () =>
      trimFrom(analytics?.messagesPerDay ?? []).map((d) => ({
        label: d.date,
        value: d.value,
      })),
    [analytics, firstDataDate],
  );

  const latencyByParticipant = useMemo(() => {
    const raw = analytics?.latencyByParticipant ?? {};
    if (!firstDataDate) return raw;
    const trimmed: Record<string, LatencyPoint[]> = {};
    for (const [p, pts] of Object.entries(raw)) {
      trimmed[p] = pts.filter((d) => d.date >= firstDataDate);
    }
    return trimmed;
  }, [analytics, firstDataDate]);

  const allDates = useMemo(
    () => trimFrom(analytics?.chatsPerDay ?? []).map((d) => d.date),
    [analytics, firstDataDate],
  );

  if (accountsLoading) return <Loading text="Loading accounts..." fullPage />;
  if (accountsError) return <ErrorMessage error={accountsError} />;
  if (!currentAccount) {
    return (
      <ErrorMessage
        error={new Error("Groups is currently available to admins only.")}
      />
    );
  }

  const totalChats = chatsPerDay.reduce((sum, d) => sum + d.value, 0);
  const totalUsers = usersPerDay.reduce((sum, d) => sum + d.value, 0);
  const totalMessages = messagesPerDay.reduce((sum, d) => sum + d.value, 0);

  const analyticsContent = loading
    ? <Loading text="Loading analytics..." />
    : error
    ? (
      <div style={{ padding: 40, textAlign: "center" }}>
        <ErrorMessage error={error} />
        <button
          type="button"
          onClick={() => {
            setError(null);
            setLoading(true);
            globalThis.location.reload();
          }}
          style={{
            marginTop: 20,
            padding: "10px 20px",
            background: th.primary,
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    )
    : (
      <>
        <div
          style={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          {periods.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setPeriodDays(p.days)}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: `1px solid ${th.inputBorder}`,
                background: periodDays === p.days ? "#3a7bd5" : th.inputBg,
                color: periodDays === p.days ? "#fff" : th.textMuted,
                cursor: "pointer",
                fontWeight: periodDays === p.days ? 600 : 400,
                fontSize: 13,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 24,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              background: th.cardBgAccent,
              borderRadius: 10,
              padding: "16px 24px",
              flex: 1,
              minWidth: 140,
            }}
          >
            <div style={{ color: th.textDim, fontSize: 12 }}>
              Total conversations
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: th.statBlue }}>
              {totalChats}
            </div>
          </div>
          <div
            style={{
              background: th.cardBgAccent,
              borderRadius: 10,
              padding: "16px 24px",
              flex: 1,
              minWidth: 140,
            }}
          >
            <div style={{ color: th.textDim, fontSize: 12 }}>
              Unique participants
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: th.statGreen }}>
              {totalUsers}
            </div>
          </div>
          <div
            style={{
              background: th.cardBgAccent,
              borderRadius: 10,
              padding: "16px 24px",
              flex: 1,
              minWidth: 140,
            }}
          >
            <div style={{ color: th.textDim, fontSize: 12 }}>
              Total messages
            </div>
            <div
              style={{ fontSize: 28, fontWeight: 700, color: th.statOrange }}
            >
              {totalMessages}
            </div>
          </div>
        </div>

        <BarChart
          data={chatsPerDay}
          title="Conversations per day"
          color="#3a7bd5"
        />
        <BarChart
          data={usersPerDay}
          title="Unique participants per day"
          color="#2ec4a0"
        />
        <BarChart
          data={messagesPerDay}
          title="Messages per day"
          color="#ff9f43"
        />
        {Object.keys(latencyByParticipant).length > 0 && (
          <LatencyChart
            data={latencyByParticipant}
            dates={allDates}
          />
        )}
      </>
    );

  const settingsContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Group Name & Renaming */}
      <div
        style={{
          background: th.cardBg,
          borderRadius: 8,
          padding: 16,
          border: `1px solid ${th.border}`,
        }}
      >
        {editingName
          ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                value={groupNameInput}
                onInput={(e) => setGroupNameInput(e.currentTarget.value)}
                className="input-field"
                style={{ marginBottom: 0, flex: 1 }}
              />
              <button
                type="button"
                className="compact-button"
                onClick={handleRenameGroup}
                disabled={renaming}
              >
                {renaming ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className="compact-button"
                onClick={() =>
                  setEditingName(false)}
              >
                Cancel
              </button>
            </div>
          )
          : (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    color: th.textDim,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: 4,
                  }}
                >
                  Group Name
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: th.textBright,
                  }}
                >
                  {selectedGroup?.name || "No Group Selected"}
                </div>
              </div>
              {selectedGroupId && (
                <button
                  type="button"
                  className="compact-button"
                  onClick={() => {
                    setGroupNameInput(selectedGroup?.name || "");
                    setEditingName(true);
                  }}
                >
                  Rename
                </button>
              )}
            </div>
          )}
        {renameError && (
          <div style={{ color: th.danger, fontSize: 13, marginTop: 6 }}>
            {renameError}
          </div>
        )}
      </div>

      {/* Group Viewers */}
      {selectedGroupId
        ? (
          <div
            style={{
              background: th.cardBg,
              borderRadius: 8,
              padding: 16,
              border: `1px solid ${th.border}`,
            }}
          >
            <EmailList
              label="Viewers (Users)"
              items={selectedGroupViewers}
              onAdd={(email) => addViewer(selectedGroupId, email)}
              onRemove={(email) => removeViewer(selectedGroupId, email)}
            />
          </div>
        )
        : (
          <div
            style={{ color: th.textFaint, textAlign: "center", padding: 20 }}
          >
            Select a specific group to manage its viewers.
          </div>
        )}
    </div>
  );

  return (
    <div
      style={{
        padding: 20,
        maxWidth: 800,
        margin: "0 auto",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <div style={{ width: "100%", maxWidth: 320 }}>
          <GroupSelector
            groups={groups}
            value={selectedGroupId}
            onChange={setSelectedGroupId}
            includeAll
          />
        </div>
      </div>

      {/* Tab headers */}
      {selectedGroupId && (
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${th.border}`,
            marginBottom: 20,
            gap: 16,
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab("settings")}
            style={{
              padding: "8px 12px",
              background: "transparent",
              border: "none",
              borderBottom: activeTab === "settings"
                ? `2px solid ${th.primary}`
                : "none",
              color: activeTab === "settings" ? th.textBright : th.textDim,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Info & Settings
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("analytics")}
            style={{
              padding: "8px 12px",
              background: "transparent",
              border: "none",
              borderBottom: activeTab === "analytics"
                ? `2px solid ${th.primary}`
                : "none",
              color: activeTab === "analytics" ? th.textBright : th.textDim,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Analytics
          </button>
        </div>
      )}

      {selectedGroupId
        ? (
          activeTab === "settings" ? settingsContent : analyticsContent
        )
        : analyticsContent}
    </div>
  );
};
